/**
 * Client for POST /api/cognitory/v2/answer.
 * Always sends cognitoryId; backend reauthorizes independently.
 * Retains full citation identity for audit; presentation labels are separate.
 */

import {
  COGNITORY_V2_ANSWER_PATH,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";
import type { CognitoryCitationIdentity } from "../presentation/cognitoryCitationPresentation";

export type CognitoryAnswerCitationView = CognitoryCitationIdentity;

export type CognitoryAnswerSupportDetails = {
  outcome: string;
  reason: string;
  conversationContext: string;
  evidenceSearch: string;
  matchingDocuments?: string;
  documents?: string[];
  answerProduced: string;
  supportCode: string;
};

export type CognitoryAnswerConversationScope = {
  scopeDocumentIds: string[];
  scopeSource: "explicit_document" | "active_subject" | "citation_scope";
};

export type CognitoryAnswerClientResult =
  | {
      kind: "answered";
      requestId: string;
      answerText: string;
      citations: CognitoryAnswerCitationView[];
      conversationScope?: CognitoryAnswerConversationScope;
    }
  | {
      kind: "refused";
      requestId: string;
      refusalText: string;
      supportDetails?: CognitoryAnswerSupportDetails;
      conversationScope?: CognitoryAnswerConversationScope;
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "temporary_failure"
        | "answer_not_supported"
        | "unable_to_answer";
      requestId: string | null;
      conversationScope?: CognitoryAnswerConversationScope;
    };

function optionalString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function optionalNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function supportDetailsView(
  value: unknown,
): CognitoryAnswerSupportDetails | undefined {
  if (!value || typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  if (
    typeof record.outcome !== "string" ||
    typeof record.reason !== "string" ||
    typeof record.conversationContext !== "string" ||
    typeof record.evidenceSearch !== "string" ||
    typeof record.answerProduced !== "string" ||
    typeof record.supportCode !== "string"
  ) {
    return undefined;
  }
  const documents = Array.isArray(record.documents)
    ? record.documents.filter(
        (entry): entry is string => typeof entry === "string",
      )
    : undefined;
  return {
    outcome: record.outcome,
    reason: record.reason,
    conversationContext: record.conversationContext,
    evidenceSearch: record.evidenceSearch,
    ...(typeof record.matchingDocuments === "string"
      ? { matchingDocuments: record.matchingDocuments }
      : {}),
    ...(documents && documents.length > 0 ? { documents } : {}),
    answerProduced: record.answerProduced,
    supportCode: record.supportCode,
  };
}

function conversationScopeView(
  value: unknown,
): CognitoryAnswerConversationScope | undefined {
  if (!value || typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  if (
    record.scopeSource !== "explicit_document" &&
    record.scopeSource !== "active_subject" &&
    record.scopeSource !== "citation_scope"
  ) {
    return undefined;
  }
  if (!Array.isArray(record.scopeDocumentIds)) return undefined;
  const scopeDocumentIds = record.scopeDocumentIds.filter(
    (documentId): documentId is string => typeof documentId === "string",
  );
  if (scopeDocumentIds.length === 0) return undefined;
  return {
    scopeDocumentIds,
    scopeSource: record.scopeSource,
  };
}

function citationView(value: unknown): CognitoryAnswerCitationView | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  if (typeof record.citationLabel !== "string" || !record.citationLabel) {
    return null;
  }
  return {
    citationLabel: record.citationLabel,
    documentId: optionalString(record.documentId),
    documentVersionId: optionalString(record.documentVersionId),
    chunkId: optionalString(record.chunkId),
    sourcePath: optionalString(record.sourcePath),
    sourceCharStart: optionalNumber(record.sourceCharStart),
    sourceCharEnd: optionalNumber(record.sourceCharEnd),
    evidenceSpanStart: optionalNumber(record.evidenceSpanStart),
    evidenceSpanEnd: optionalNumber(record.evidenceSpanEnd),
    verificationReference: optionalString(record.verificationReference),
  };
}

export type CognitoryConversationRecentTurn = {
  question: string;
  requestId?: string;
  citationDocumentIds: string[];
  scopeDocumentIds?: string[];
  scopeSource?: CognitoryAnswerConversationScope["scopeSource"];
};

export type CognitoryConversationContext = {
  recentTurns: CognitoryConversationRecentTurn[];
};

export async function submitCognitoryAnswer(input: {
  cognitoryId: string;
  question: string;
  conversationContext?: CognitoryConversationContext;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<CognitoryAnswerClientResult> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const token =
    input.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", requestId: null };
  }

  const base = input.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  let response: Response;
  try {
    response = await fetchImpl(`${base}${COGNITORY_V2_ANSWER_PATH}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      cache: "no-store",
      body: JSON.stringify({
        cognitoryId: input.cognitoryId,
        question: input.question,
        ...(input.conversationContext &&
        input.conversationContext.recentTurns.length > 0
          ? {
              conversationContext: {
                recentTurns: input.conversationContext.recentTurns.map(
                  turn => ({
                    question: turn.question,
                    citationDocumentIds: turn.citationDocumentIds,
                    ...(turn.requestId ? { requestId: turn.requestId } : {}),
                    ...(turn.scopeDocumentIds
                      ? { scopeDocumentIds: turn.scopeDocumentIds }
                      : {}),
                    ...(turn.scopeSource
                      ? { scopeSource: turn.scopeSource }
                      : {}),
                  }),
                ),
              },
            }
          : {}),
      }),
    });
  } catch {
    return { kind: "temporary_failure", requestId: null };
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const requestId =
    body &&
    typeof body === "object" &&
    typeof (body as { requestId?: unknown }).requestId === "string"
      ? (body as { requestId: string }).requestId
      : null;

  const conversationScope = conversationScopeView(
    (body as { conversationScope?: unknown }).conversationScope,
  );

  if (response.status === 401) {
    return {
      kind: "unauthenticated",
      requestId,
      ...(conversationScope ? { conversationScope } : {}),
    };
  }
  if (response.status === 403) {
    return {
      kind: "forbidden",
      requestId,
      ...(conversationScope ? { conversationScope } : {}),
    };
  }
  if (response.status === 503) {
    return {
      kind: "temporary_failure",
      requestId,
      ...(conversationScope ? { conversationScope } : {}),
    };
  }
  if (response.status === 422) {
    return {
      kind: "answer_not_supported",
      requestId,
      ...(conversationScope ? { conversationScope } : {}),
    };
  }

  if (!body || typeof body !== "object") {
    return { kind: "unable_to_answer", requestId };
  }

  const decision = (body as { decision?: unknown }).decision;
  if (decision === "answer_rendered") {
    const answerText = (body as { answerText?: unknown }).answerText;
    if (typeof answerText !== "string") {
      return { kind: "unable_to_answer", requestId };
    }
    const rawCitations = (body as { citations?: unknown }).citations;
    const citations: CognitoryAnswerCitationView[] = [];
    if (Array.isArray(rawCitations)) {
      for (const item of rawCitations) {
        const view = citationView(item);
        if (view) citations.push(view);
      }
    }
    return {
      kind: "answered",
      requestId: requestId ?? "unknown",
      answerText,
      citations,
      ...(conversationScope ? { conversationScope } : {}),
    };
  }

  if (decision === "refusal_rendered") {
    const refusalText = (body as { refusalText?: unknown }).refusalText;
    if (typeof refusalText !== "string") {
      return { kind: "unable_to_answer", requestId };
    }
    const supportDetails = supportDetailsView(
      (body as { supportDetails?: unknown }).supportDetails,
    );
    return {
      kind: "refused",
      requestId: requestId ?? "unknown",
      refusalText,
      ...(supportDetails ? { supportDetails } : {}),
      ...(conversationScope ? { conversationScope } : {}),
    };
  }

  if (decision === "temporary_failure") {
    return {
      kind: "temporary_failure",
      requestId,
      ...(conversationScope ? { conversationScope } : {}),
    };
  }
  if (decision === "answer_not_supported") {
    return {
      kind: "answer_not_supported",
      requestId,
      ...(conversationScope ? { conversationScope } : {}),
    };
  }
  if (decision === "forbidden" || decision === "unauthenticated") {
    return {
      kind: decision === "forbidden" ? "forbidden" : "unauthenticated",
      requestId,
    };
  }

  return { kind: "unable_to_answer", requestId };
}
